import { FileText, Upload, X } from "lucide-react";
import type { PayslipRecord } from "../../types/payrollAdmin.js";
import { Button } from "../ui/index.js";
import { monthLabel } from "./PeriodPickers.js";
import { payslipRequiresReplacement } from "../../routes/payrollInputIntegrity.js";
import {
  payslipDuplicateDisposition,
} from "../../routes/payrollWorkflow.js";
export const payslipStatusLabel = (status: string) => {
  if (status === "matched") return "Matched";
  if (status === "manual_resolved") return "Manually resolved";
  if (status === "duplicate") return "Duplicate";
  if (status === "period_mismatch") return "Period mismatch";
  if (status === "ineligible") return "Excluded this month";
  if (status === "split_review") return "Split review";
  if (status === "failed") return "Failed";
  if (status === "replaced") return "Replaced";
  return "Unmatched";
};

export const payslipStatusTone = (status: string) => {
  if (status === "matched" || status === "manual_resolved")
    return "success" as const;
  if (
    [
      "duplicate",
      "period_mismatch",
      "period_missing",
      "unmatched",
      "ineligible",
      "split_review",
    ].includes(status)
  )
    return "warning" as const;
  if (status === "failed") return "danger" as const;
  return "neutral" as const;
};

export type PayslipReviewCategory = "attention" | "ready" | "complete";

export const payslipReviewCategoryForStatus = (
  status: string,
): PayslipReviewCategory => {
  if (
    [
      "Needs checking",
      "Wrong month",
      "Replacement required",
      "Rejected",
      "No payslip",
    ].includes(status)
  )
    return "attention";
  if (["Ready to confirm", "Accepted"].includes(status)) return "ready";
  return "complete";
};

export const payrollLoadErrorMessage = (error: unknown) => {
  const message = error instanceof Error ? error.message.trim() : "";
  if (/\b(?:404|not found)\b/i.test(message))
    return "The payroll service did not return this data.";
  return message || "Check the payroll service and try again.";
};

export const payslipIssueContext = (record: PayslipRecord) => {
  const parserEvidence = record.errorMessage || record.warnings[0] || "";
  const failureEvidence = parserEvidence || "The PDF could not be read.";
  const detectedName =
    record.parsedEmployeeName || "No employee name was extracted";
  const detectedCode =
    record.parsedPayrollCode || "No payroll code was extracted";
  const identityEvidence = `Detected name: ${detectedName}. Detected payroll code: ${detectedCode}.`;

  if (payslipRequiresReplacement(record)) {
    const privacyBlocked =
      record.validationCodes.includes("multiple_employees") ||
      record.validationCodes.includes("ambiguous_payslip_split");
    const noReadableText = record.validationCodes.includes("pdf_no_text");
    return {
      title: privacyBlocked
        ? "Payslip boundary could not be verified"
        : noReadableText
          ? "No readable text"
          : "PDF must be replaced",
      instruction: privacyBlocked
        ? `The section detected for ${detectedName} may overlap another employee's payslip. It cannot be confirmed safely. Upload a replacement PDF with clearly separated payslips.`
        : noReadableText
          ? "This PDF does not contain extractable text. Upload the original text-based PDF."
          : "Upload a valid text-based PDF.",
      evidence: [identityEvidence, parserEvidence].filter(Boolean).join(" "),
      action: "replace" as const,
    };
  }

  if (
    record.matchStatus === "duplicate" &&
    record.crossMonthRecoveryAvailable
  ) {
    return {
      title: `Stored under ${monthLabel(record.duplicateSource?.month ?? "")}`,
      instruction: `No employee correction is needed. Use the batch recovery action above to place this PDF in ${monthLabel(record.month)} and remove the wrong-month review records.`,
      evidence: parserEvidence,
      action: "recover-cross-month" as const,
    };
  }

  if (
    record.matchStatus === "duplicate" &&
    payslipDuplicateDisposition(record) === "protected-cross-month"
  ) {
    return {
      title: `Stored under ${monthLabel(record.duplicateSource?.month ?? "")}`,
      instruction:
        "The earlier record was already confirmed or manually corrected, so it cannot be moved automatically without losing audited work. Open View and review this payslip individually.",
      evidence: parserEvidence,
      action: "review-protected-cross-month" as const,
    };
  }

  if (
    record.warnings.some((warning) =>
      warning.toLowerCase().includes("payroll code was not found"),
    )
  ) {
    return {
      title: "Payroll code could not be extracted",
      instruction: `The PDF contains the name ${detectedName}, but its payroll-code field is blank. The employee is fixed by the exact name match. Open View to check the highlighted field, then add a reason and confirm the employee.`,
      evidence: [identityEvidence, parserEvidence].filter(Boolean).join(" "),
      action: "resolve" as const,
    };
  }

  if (
    record.matchStatus === "ineligible" ||
    record.validationCodes.includes("employee_ineligible")
  ) {
    return {
      title: "Employee excluded this month",
      instruction:
        "This exact Work Profile exists, but it is not eligible for the selected payroll month. Review the profile period; this payslip cannot be confirmed.",
      evidence: [identityEvidence, parserEvidence].filter(Boolean).join(" "),
      action: "review-profile" as const,
    };
  }

  if (record.matchStatus === "manual_resolved" && !record.calculationReady) {
    return {
      title: "Payroll values are missing",
      instruction: "Upload a clearer PDF.",
      evidence: parserEvidence,
      action: "replace" as const,
    };
  }
  if (record.matchStatus === "duplicate") {
    return {
      title: "Possible duplicate",
      instruction: "Choose this employee only to replace the existing PDF.",
      evidence: parserEvidence,
      action: "resolve" as const,
    };
  }
  if (record.matchStatus === "period_mismatch") {
    return {
      title: "Check the payslip month",
      instruction: `PDF: ${record.parsedPeriod || "unknown"} · Review: ${record.month}`,
      evidence: parserEvidence,
      action: "resolve" as const,
    };
  }
  if (record.matchStatus === "split_review") {
    return {
      title: "Check PDF pages",
      instruction: "Open the PDF, then choose the employee.",
      evidence: parserEvidence,
      action: "resolve" as const,
    };
  }
  if (record.matchStatus === "failed" || record.parserStatus === "failed") {
    return {
      title: "PDF cannot be read",
      instruction: "Upload a clearer PDF.",
      evidence: failureEvidence,
      action: "replace" as const,
    };
  }
  return {
    title: record.parsedPayrollCode
      ? "Payroll code does not match the Work profile"
      : "Payroll code could not be extracted",
    instruction: record.parsedPayrollCode
      ? `The PDF contains ${detectedName} with payroll code ${record.parsedPayrollCode}, but that code is missing from the Work profile. The employee is fixed by the exact name match. Verify the highlighted code, then add a reason and confirm the employee.`
      : `The PDF contains the name ${detectedName}, but no payroll code. The employee is fixed by the exact name match. Verify the highlighted missing field, then add a reason and confirm the employee.`,
    evidence: [identityEvidence, parserEvidence].filter(Boolean).join(" "),
    action: "resolve" as const,
  };
};

export const PayslipDropZone = ({
  canUpload,
  compact = false,
  files,
  dragActive,
  uploading,
  onDragActiveChange,
  onFilesAdded,
  onRemove,
  onUpload,
}: {
  canUpload: boolean;
  compact?: boolean;
  files: File[];
  dragActive: boolean;
  uploading: boolean;
  onDragActiveChange: (active: boolean) => void;
  onFilesAdded: (files: File[]) => void;
  onRemove: (file: File) => void;
  onUpload: () => void;
}) => {
  const canEditSelection = canUpload && !uploading;
  return (
    <section
      aria-labelledby="payslip-upload-title"
      aria-busy={uploading}
      className="h-full min-w-0"
    >
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          if (canEditSelection) onDragActiveChange(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (canEditSelection) onDragActiveChange(true);
        }}
        onDragLeave={() => onDragActiveChange(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDragActiveChange(false);
          if (canEditSelection)
            onFilesAdded(Array.from(event.dataTransfer.files));
        }}
        className={`flex h-full ${compact ? "min-h-28" : "min-h-64"} flex-col rounded-xl border border-dashed bg-surface p-4 transition-colors ${dragActive ? "border-accent bg-accent-subtle" : "border-border bg-background"} ${canEditSelection ? "" : "opacity-50"}`}
      >
        <label
          htmlFor="guided-payslip-upload"
          aria-disabled={!canEditSelection}
          className={`flex ${compact ? "min-h-20 px-3 py-2" : "min-h-40 px-4 py-5"} flex-1 flex-col items-center justify-center rounded-lg text-center focus-within:ring-2 focus-within:ring-accent ${canEditSelection ? "cursor-pointer" : "cursor-not-allowed"}`}
        >
          {
            <span
              className="mb-2 flex size-8 items-center justify-center text-accent"
              aria-hidden="true"
            >
              <Upload size={20} />
            </span>
          }
          <span
            id="payslip-upload-title"
            className={`${compact ? "" : "mt-3"} text-sm font-semibold text-foreground`}
          >
            {compact ? "Add more payslip PDFs" : "Drop payslip PDFs here"}
          </span>
          <span className="mt-1 text-xs leading-5 text-foreground-muted">
            or{" "}
            <span className="font-semibold text-accent underline underline-offset-2">
              choose files from your computer
            </span>
          </span>
          {!compact && (
            <span className="mt-1 text-[11px] text-foreground-subtle">
              PDF files only · individual or combined text-based payslips
            </span>
          )}
          <input
            id="guided-payslip-upload"
            type="file"
            accept="application/pdf,.pdf"
            multiple
            disabled={!canEditSelection}
            onChange={(event) => {
              if (canEditSelection)
                onFilesAdded(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
            className="sr-only"
          />
        </label>
        {files.length > 0 && (
          <div className="border-t border-border pt-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p
                className="text-xs font-semibold text-foreground"
                aria-live="polite"
              >
                {files.length} {files.length === 1 ? "PDF" : "PDFs"} ready to
                upload
              </p>
              <Button
                type="button"
                onClick={onUpload}
                disabled={uploading || !canUpload}
                className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-fg hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Upload size={16} aria-hidden="true" />
                {uploading ? "Uploading…" : `Upload ${files.length}`}
              </Button>
            </div>
            <ul
              className="mt-2 grid max-h-32 gap-1 overflow-y-auto"
              aria-label="Selected payslip PDFs"
            >
              {files.map((file) => (
                <li
                  key={`${file.name}:${file.size}:${file.lastModified}`}
                  className="flex min-w-0 items-center justify-between gap-3 py-1 text-xs text-foreground-muted"
                >
                  <span className="min-w-0 truncate">
                    <FileText
                      size={14}
                      className="mr-2 inline shrink-0 text-foreground-subtle"
                      aria-hidden="true"
                    />
                    {file.name}
                  </span>
                  <Button variant="ghost" size="sm"
                    type="button"
                    onClick={() => onRemove(file)}
                    disabled={!canEditSelection}
                    className="inline-flex min-h-8 shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-foreground-subtle hover:bg-elevated hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X size={15} className="shrink-0" aria-hidden="true" />
                    <span>Remove</span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export { PohodaWorkbookPreviewTable } from "./PohodaWorkbookPreview.js";
